import { BASE } from '../api/client'
import { tryRefreshToken } from '../api/client'

export interface CallSignal {
  type: string
  room_id?: string
  from?: string
  to?: string
  payload?: unknown
}

type SignalHandler = (sig: CallSignal) => void

const MAX_RETRIES = 8
const BASE_DELAY = 2000 // ms

class CallsWsClient {
  private ws: WebSocket | null = null
  private handlers: Set<SignalHandler> = new Set()
  private token: string | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private shouldReconnect = false
  private retries = 0
  // Track when the WS was opened to detect immediate-close (= 401-like)
  private openedAt = 0

  connect(token: string) {
    this.token = token
    if (this.ws?.readyState === WebSocket.OPEN) return
    this.shouldReconnect = true
    this.retries = 0
    this.open()
  }

  private open() {
    if (!this.shouldReconnect || !this.token) return
    const base = BASE.replace(/^http/, 'ws')
    const url = `${base}/v1/calls/ws?token=${this.token}`
    this.openedAt = Date.now()
    const ws = new WebSocket(url)
    this.ws = ws

    ws.onmessage = (e) => {
      try {
        const sig = JSON.parse(e.data) as CallSignal
        this.handlers.forEach((h) => h(sig))
      } catch { /* ignore malformed */ }
    }

    ws.onopen = () => {
      // Successful connection — reset retry counter
      this.retries = 0
    }

    ws.onclose = () => {
      if (!this.shouldReconnect || ws !== this.ws) return
      this.scheduleReconnect()
    }

    ws.onerror = () => {
      ws.close()
    }
  }

  private async scheduleReconnect() {
    if (this.retries >= MAX_RETRIES) {
      // Give up — token is likely permanently invalid or service is down
      this.shouldReconnect = false
      return
    }

    // If the connection closed within 1 s of opening it's likely a 401 —
    // try to refresh the token before reconnecting
    const wasImmediate = Date.now() - this.openedAt < 1000
    if (wasImmediate) {
      const ok = await tryRefreshToken()
      if (!ok) {
        this.shouldReconnect = false
        return
      }
      // Use the freshly refreshed token on next open()
      this.token = localStorage.getItem('access_token')
    }

    this.retries++
    const delay = Math.min(BASE_DELAY * 2 ** (this.retries - 1), 30000)
    this.reconnectTimer = setTimeout(() => this.open(), delay)
  }

  disconnect() {
    this.shouldReconnect = false
    this.retries = 0
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.ws?.close()
    this.ws = null
  }

  send(sig: CallSignal) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(sig))
    }
  }

  on(handler: SignalHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }
}

export const callsWs = new CallsWsClient()
